const normalizeLineBreaks = (value = '') =>
  String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u2028/g, '\n')
    .replace(/\u2029/g, '\n');

const cleanTitle = (value = '') =>
  String(value || '')
    .replace(/^\{+/, '')
    .replace(/_+\s*$/, '')
    .trim();

const splitKwTags = (value = '') =>
  String(value || '')
    .split(/\s*-\s*/)
    .map((part) => String(part || '').trim().replace(/^\[+/, '').replace(/\]+$/, ''))
    .filter(Boolean);

const uniq = (values = []) => [...new Set((values || []).map((value) => String(value || '').trim()).filter(Boolean))];

const parseKwDirective = (line = '') => {
  const trimmed = String(line || '').trim();
  const prefixed = trimmed.match(/^(\+)?\s*(\*{1,2})\s*kw::\s*(.+)\s*$/i);
  if (prefixed) {
    return {
      includeSelf: Boolean(prefixed[1]),
      stars: Math.min(2, String(prefixed[2] || '').length),
      tags: splitKwTags(prefixed[3] || ''),
    };
  }
  const plain = trimmed.match(/^\s*kw::\s*(.+)\s*$/i);
  if (!plain) return null;
  return {
    includeSelf: true,
    stars: 0,
    tags: splitKwTags(plain[1] || ''),
  };
};

const parseTagsAndBody = (value = '') => {
  const currentTags = [];
  const childTags = [];
  const descendantTags = [];
  const keptLines = [];

  normalizeLineBreaks(value)
    .split('\n')
    .forEach((line) => {
      const trimmed = line.trim();
      const directive = parseKwDirective(trimmed);
      if (directive) {
        const { includeSelf, stars, tags } = directive;
        if (stars === 0) {
          currentTags.push(...tags);
          return;
        }
        if (stars === 1) {
          childTags.push(...tags);
          if (includeSelf) currentTags.push(...tags);
          return;
        }
        descendantTags.push(...tags);
        if (includeSelf) currentTags.push(...tags);
        return;
      }
      // Urtext dynamic/inline instruction blocks (e.g. [[...TREE(...)]]), not note content.
      if (/^\s*\[\[.*\]\]\s*$/.test(trimmed)) return;
      if (!trimmed) {
        keptLines.push('');
        return;
      }
      const normalizedLine = String(line || '')
        .replace(/\t/g, ' ')
        .replace(/\u00a0/g, ' ')
        .trimEnd()
        .trimStart();
      keptLines.push(normalizedLine);
    });

  const compacted = [];
  let blankRun = 0;
  keptLines.forEach((line) => {
    if (!String(line || '').trim()) {
      blankRun += 1;
      if (blankRun > 1) return; // keep at most one blank line
      compacted.push('');
      return;
    }
    blankRun = 0;
    compacted.push(line);
  });

  while (compacted.length && !String(compacted[0] || '').trim()) compacted.shift();
  while (compacted.length && !String(compacted[compacted.length - 1] || '').trim()) compacted.pop();
  const body = compacted.join('\n').trim();
  return {
    currentTags: uniq(currentTags),
    childTags: uniq(childTags),
    descendantTags: uniq(descendantTags),
    body,
  };
};

const extractRootTitle = (rootText = '') => {
  const lines = normalizeLineBreaks(rootText).split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (!trimmed || trimmed.startsWith('{')) continue;
    if (/_\s*$/.test(trimmed)) {
      const title = cleanTitle(trimmed);
      lines[i] = '';
      return { title, remaining: lines.join('\n') };
    }
  }
  return { title: '', remaining: rootText };
};

const shouldIgnoreNode = (node = {}) => {
  const title = String(node.title || '').trim().toLowerCase();
  const body = String(node.body || '');
  if (!title && !body.trim()) return true;
  if (title.startsWith('~')) return true;
  if (/\[\[.*tree\s*\(/i.test(body)) return true;
  const treeGlyphHits = body.split('\n').filter((line) => /[├└│]──/.test(line)).length;
  if (treeGlyphHits >= 3 && !/[a-zA-Z]/.test(title.replace(/^~/, '').trim())) return true;
  return false;
};

const parseHeaderTitle = (header = '') => {
  const stripped = String(header || '').trim();
  if (!stripped) return '';
  return cleanTitle(stripped);
};

const parseBraceNode = (text, startIndex = 0) => {
  let i = startIndex;
  if (text[i] !== '{') return null;
  i += 1;

  let header = '';
  while (i < text.length) {
    const ch = text[i];
    if (ch === '\n' || ch === '}' || ch === '{') break;
    header += ch;
    i += 1;
  }

  const childNodes = [];
  let ownBody = '';

  while (i < text.length) {
    const ch = text[i];
    if (ch === '{') {
      const child = parseBraceNode(text, i);
      if (!child) break;
      childNodes.push(child.node);
      i = child.endIndex;
      continue;
    }
    if (ch === '}') {
      i += 1;
      break;
    }
    ownBody += ch;
    i += 1;
  }

  return {
    node: {
      title: parseHeaderTitle(header),
      body: ownBody,
      children: childNodes,
    },
    endIndex: i,
  };
};

const parseUrtextTree = (value = '') => {
  const text = normalizeLineBreaks(value);
  const topNodes = [];
  let rootText = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] === '{') {
      const parsed = parseBraceNode(text, i);
      if (!parsed) {
        rootText += text[i];
        i += 1;
        continue;
      }
      topNodes.push(parsed.node);
      i = parsed.endIndex;
      continue;
    }
    rootText += text[i];
    i += 1;
  }
  return { topNodes, rootText };
};

const flattenNodes = (
  nodes = [],
  parentPath = [],
  inheritedChildTags = [],
  inheritedDescendantTags = []
) => {
  const entries = [];
  nodes.forEach((node, index) => {
    if (shouldIgnoreNode(node)) return;
    const title = cleanTitle(node.title || '');
    const currentPath = [...parentPath, title].filter(Boolean);
    const parsed = parseTagsAndBody(node.body || '');
    const entryTags = uniq([
      ...inheritedChildTags,
      ...inheritedDescendantTags,
      ...parsed.currentTags,
    ]);
    entries.push({
      title: title || '(untitled)',
      notes: parsed.body,
      tags: entryTags,
      createdAtIso: '',
      urtextPath: currentPath.join(' > '),
      parentUrtextPath: parentPath.length ? parentPath.join(' > ') : '',
      urtextDepth: Math.max(0, currentPath.length - 1),
      urtextOrder: index,
    });
    const nextInheritedChildTags = parsed.childTags;
    const nextInheritedDescendantTags = uniq([
      ...inheritedDescendantTags,
      ...parsed.descendantTags,
    ]);
    entries.push(
      ...flattenNodes(
        node.children || [],
        currentPath,
        nextInheritedChildTags,
        nextInheritedDescendantTags
      )
    );
  });
  return entries;
};

export const parseBulkUrtextText = (value = '') => {
  const normalized = normalizeLineBreaks(value).trim();
  if (!normalized) return [];

  const { topNodes, rootText } = parseUrtextTree(normalized);
  const { title: rootTitle, remaining } = extractRootTitle(rootText);
  const rootParsed = parseTagsAndBody(remaining);
  const entries = [];

  if (rootTitle || rootParsed.body || rootParsed.currentTags.length) {
    entries.push({
      title: rootTitle || 'Imported Urtext Note',
      notes: rootParsed.body,
      tags: rootParsed.currentTags,
      createdAtIso: '',
      urtextPath: rootTitle || '',
      parentUrtextPath: '',
      urtextDepth: 0,
      urtextOrder: 0,
    });
  }

  const seedPath = rootTitle ? [rootTitle] : [];
  entries.push(...flattenNodes(topNodes, seedPath, rootParsed.childTags, rootParsed.descendantTags));
  return entries.filter((entry) => String(entry.title || '').trim());
};
